import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography, LinearProgressDeterminate} from 'views/components';

const ProgressIndicator = (props) => {
  const {totalSteps, curStep, sections} = props;

  // Calculate percentage completion
  const percentComplete = Math.round((curStep / totalSteps) * 100);

  const stepLabels = sections.map((section) => section.label);
  // Generate array of step numbers for numbered icons
  const steps = Array.from({length: totalSteps}, (_, index) => index + 1);

  return (
    <Stack spacing={1}>
      {/* Top layer: Step x of y and percentage */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="body1" color="text.primary">
          Step {curStep} of {totalSteps}
        </Typography>
        <Typography variant="body1" color="primary.main">
          {percentComplete}% complete
        </Typography>
      </Box>

      {/* Middle layer: Progress bar */}
      <Box sx={{display: 'flex', justifyContent: 'center'}}>
        <LinearProgressDeterminate
          value={percentComplete}
          sx={{
            'width': '100%',
            'mr': 0,
            'height': 8,
            'borderRadius': 4,
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
            },
          }}
        />
      </Box>

      {/* Bottom layer: Numbered step icons with labels */}
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        {
          steps.map((stepNumber) => {
            const isCompleted = stepNumber < curStep;
            const isActive = stepNumber === curStep;
            const stepLabel = stepLabels[stepNumber - 1] || '';

            return (
              <Stack
                key={stepNumber}
                spacing={0.5}
                sx={{
                  alignItems: 'center',
                  minWidth: 0, // Allow text to wrap if needed
                }}
              >
                <Box
                  sx={
                    (theme) => ({
                      minWidth: 30,
                      height: 30,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isCompleted ?
                        theme.palette.primary.main :
                        isActive ?
                          theme.palette.primary.main :
                          theme.palette.grey[300],
                      color: isCompleted || isActive ?
                        theme.palette.primary.contrastText :
                        theme.palette.text.secondary,
                      fontWeight: isActive ? 'bold' : 'normal',
                      border: isActive ?
                        `3px solid #EEEEEE` :
                        'none',
                      boxShadow: isActive ?
                        `0 0 0 2px ${theme.palette.primary.light}` :
                        'none',
                      transition: 'all 0.3s ease-in-out',
                    })
                  }
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'inherit',
                      fontSize: isActive ? '0.875rem' : '0.75rem',
                    }}
                  >
                    {stepNumber}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={
                    (theme) => ({
                      display: {
                        xs: 'none',
                        sm: 'block',
                        md: 'block',
                        lg: 'block',
                        xl: 'block',
                      },
                      textAlign: 'center',
                      color: isActive ?
                        theme.palette.primary.main :
                        theme.palette.text.secondary,
                      fontWeight: isActive ? 'medium' : 'normal',
                      fontSize: '0.6875rem',
                      lineHeight: 1.2,
                    })
                  }
                >
                  {stepLabel}
                </Typography>
              </Stack>
            );
          })
        }
      </Stack>
    </Stack>
  );
};

ProgressIndicator.propTypes = {
  totalSteps: PropTypes.number.isRequired,
  curStep: PropTypes.number.isRequired,
  sections: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default ProgressIndicator;
